import { levelKeys, levelValue } from '@/helper/escalationMatrixForm';
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import jwtDecode from 'jwt-decode';

import authService from '@/services/authService';
import qmsService from '@/services/qmsService';
import { isQMSAdminRole } from '@/helper/navBarHelper';

const state = {
  authKey: localStorage.getItem('auth-key') || '',
  loading: false,
  isLoggedIn: false,
  user: localStorage.getItem('auth-key') ? jwtDecode(localStorage.getItem('auth-key')) : null
};

const actions = {
  async login({ dispatch, commit, state }, { mobile, password, router }) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');

    try {
      const response = await authService.login(mobile, password);
      commit('loginSuccess', { authKey: response.data.auth_key });
      // Keep Vuex darkMode in sync with localStorage reset in loginSuccess
      dispatch('darkMode/changeDarkMode', { isDarkMode: false }, { root: true });
      dispatch('alert/success', { showType: 'toast', text: '', title: response.message, position: 'center' }, { root: true });

      const loggedInRoles = (state.user?.roles || [])
        .map(role => String(role && role.name ? role.name : '').trim().toLowerCase())
        .filter(Boolean);

      let hasEscalationMatrixRole = false;
      if (loggedInRoles.length) {
        try {
          const matrixDoc = await qmsService.getEscalationMatrix({ key: 'hr_management' });
          const matrix = Array.isArray(matrixDoc && matrixDoc.matrix) ? matrixDoc.matrix : [];
          const matrixRoles = matrix
            .flatMap(row => levelKeys(row).map(level => levelValue(row, level)?.role))
            .map(role => String(role || '').trim().toLowerCase())
            .filter(Boolean);
          localStorage.setItem('qms-matrix-roles', JSON.stringify([...new Set(matrixRoles)]));
          hasEscalationMatrixRole = matrixRoles.some(role => loggedInRoles.includes(role));
        } catch (_matrixError) {
          localStorage.removeItem('qms-matrix-roles');
        }
      } else {
        localStorage.removeItem('qms-matrix-roles');
      }

      const isQMSAdmin = isQMSAdminRole(state.user);
      router.push(isQMSAdmin || hasEscalationMatrixRole ? '/qms' : '/');
    } catch (e) {
      commit('loginFailure');
      dispatch('common/handleServiceException', { e, router }, { root: true });
    }
  },
  logout({ dispatch, commit }, { router, silent = false }) {
    dispatch('alert/clear', {}, { root: true });
    dispatch('popPrint/removeCustomLocalPopList', {}, { root: true });
    dispatch('notification/clearNotifications', {}, { root: true });
    dispatch('dishaChat/reset', {}, { root: true });
    commit('logout');
    // localStorage darkMode is cleared in logout mutation; sync Vuex so auth pages
    // leave Bootstrap dark theme (fixes QMS checkbox / login form after theme toggle)
    dispatch('darkMode/changeDarkMode', { isDarkMode: false }, { root: true });

    if (!silent) {
      dispatch('alert/success', { showType: 'toast', position: 'center', title: 'You are successfully logged out.' }, { root: true });

      // https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
      router.push('/').catch(_e => {});
    }
  },
  sessionExpired({ dispatch, commit }, { router }) {
    dispatch('alert/clear', {}, { root: true });
    dispatch('notification/clearNotifications', {}, { root: true });
    dispatch('dishaChat/reset', {}, { root: true });
    commit('logout');
    dispatch('darkMode/changeDarkMode', { isDarkMode: false }, { root: true });

    dispatch(
      'alert/error',
      { showType: 'toast', title: 'Session expired', text: 'Please login with your account.' },
      { root: true }
    );

    router.push('/login');
  },
  handleAuthMessageKey({ _dispatch }, { messageKey }) {
    switch (messageKey) {
      default:
        break;
    }
  }
};

const getters = {
  isLoggedIn: state => () => {
    if (state.isLoggedIn) {
      return true;
    }

    if (_.isEmpty(state.authKey)) {
      return false;
    }

    let decoded = null;
    try {
      decoded = jwtDecode(state.authKey);
    } catch (e) {
      return false;
    }

    if (decoded.exp && moment.unix(decoded.exp).isAfter()) {
      axios.defaults.headers.common.Authorization = state.authKey;
      return true;
    }

    return false;
  },
  getUserData(state) {
    return state.user;
  },
  isSm(state) {
    const userData = state.user;
    const isTeam = userData?.roles?.filter(role => role.name === 'STORE_SALES_MANAGER');
    if (isTeam !== null && isTeam !== undefined && isTeam.length > 0) {
      return true;
    }
    return false;
  },
  isFoodSafetyTeam(state) {
    const userData = state.user;
    const isTeam = userData?.roles?.filter(
      role =>
        role.name === 'QUALITY_AUDIT_EXECUTIVE' ||
        role.name === 'QUALITY_AUDIT_MANAGER' ||
        role.name === 'QUALITY_AUDIT_HEAD'
    );
    if (isTeam !== null && isTeam !== undefined && isTeam.length > 0) {
      return true;
    }
    return false;
  },
  isFoodSafetyManager(state) {
    const userData = state.user;
    const isAdmin = userData?.roles?.filter(role => role.name === 'QUALITY_AUDIT_MANAGER');
    if (isAdmin !== null && isAdmin !== undefined && isAdmin.length > 0) {
      return true;
    }
    return false;
  },
  isFoodSafetyAdmin(state) {
    const userData = state.user;
    const manager = userData?.roles?.filter(role => role.name === 'QUALITY_AUDIT_HEAD');
    if (manager !== null && manager !== undefined && manager.length > 0) {
      return true;
    }
    return false;
  },
  isFoodSafetyExecutive(state) {
    const userData = state.user;
    const manager = userData?.roles?.filter(role => role.name === 'QUALITY_AUDIT_EXECUTIVE');
    if (manager !== null && manager !== undefined && manager.length > 0) {
      return true;
    }
    return false;
  },
  isQMSAdmin(state) {
    return isQMSAdminRole(state.user);
  },
  /** QMS payroll reviewers may only add closure remarks when editing a query. */
  isQmsPayrollClosureRole(state) {
    const roles = state.user?.roles || [];
    const allowed = new Set(['payroll_l1', 'payroll_l2', 'c&b_l1', 'c&b_l2', 'in_store_l1', 'chro', 'qms_admin']);
    return roles.some(r => allowed.has(String(r?.name || '').toLowerCase()));
  }
};

const mutations = {
  startRequest(state) {
    state.loading = true;
    state.isLoggedIn = false;
  },
  loginSuccess(state, { authKey }) {
    state.loading = false;
    state.isLoggedIn = true;
    state.authKey = authKey;
    state.user = jwtDecode(authKey);
    localStorage.setItem('auth-key', authKey);
    localStorage.setItem('darkMode', false);
    sessionStorage.setItem('isAccountVerified', null);
    axios.defaults.headers.common.Authorization = authKey;
  },
  loginFailure(state) {
    state.loading = false;
  },
  logout(state) {
    state.isLoggedIn = false;
    state.authKey = null;
    state.user = null;
    localStorage.removeItem('auth-key');
    localStorage.removeItem('darkMode');
    axios.defaults.headers.common.Authorization = null;
    // sessionStorage.setItem('isAccountVerified', '');
    sessionStorage.clear();
  },
  clear(state) {
    state.loading = false;
    state.isLoggedIn = false;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
